import { ReactNode, forwardRef } from 'react';
import {
  TextField,
  TextFieldProps,
  InputAdornment,
  InputProps as MuiInputProps,
  Typography,
  Box,
  SxProps,
  Theme
} from '@mui/material';
import { getInputSx, inputLabelSx, labelContainerSx, requiredAsteriskSx } from './Input.styles';

export interface InputProps extends Omit<TextFieldProps, 'sx' | 'InputProps'> {
  /**
   * Icon to display at the start of the input field
   */
  startIcon?: ReactNode;
  /**
   * Icon to display at the end of the input field
   */
  endIcon?: ReactNode;
  /**
   * Label text for the input field
   */
  label?: string;
  /**
   * Custom sx styles for the input field
   */
  sx?: SxProps<Theme>;
  /**
   * Custom sx styles for the input label
   */
  labelSx?: SxProps<Theme>;
  /**
   * Custom sx styles for the start icon
   */
  startIconSx?: SxProps<Theme>;
  /**
   * Custom sx styles for the end icon
   */
  endIconSx?: SxProps<Theme>;
  /**
   * Additional InputProps to pass to the underlying TextField
   */
  InputProps?: Partial<MuiInputProps>;
}

/**
 * Reusable Input component with support for start/end icons and labels
 * 
 * @example
 * ```tsx
 * <Input
 *   label="Email"
 *   placeholder="Enter your email"
 *   startIcon={<EmailIcon />}
 *   fullWidth
 * />
 * 
 * <Input
 *   label="Password"
 *   type="password"
 *   endIcon={<VisibilityIcon />}
 *   fullWidth
 * />
 * ```
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(({
  startIcon,
  endIcon,
  label,
  sx,
  labelSx,
  startIconSx,
  endIconSx,
  InputProps,
  InputLabelProps,
  ...textFieldProps
}, ref) => {
  const mergedInputProps: Partial<MuiInputProps> = {
    ...InputProps,
    ...(startIcon && {
      startAdornment: (
        <InputAdornment position="start" sx={startIconSx}>
          {startIcon}
        </InputAdornment>
      )
    }),
    ...(endIcon && {
      endAdornment: (
        <InputAdornment position="end" sx={endIconSx}>
          {endIcon}
        </InputAdornment>
      )
    })
  };

  const mergedLabelProps = {
    ...InputLabelProps,
    sx: labelSx
      ? ([inputLabelSx, labelSx] as SxProps<Theme>)
      : inputLabelSx
  };

  const baseInputSx = getInputSx(!!startIcon);
  const mergedSx = sx
    ? ([baseInputSx, sx] as SxProps<Theme>)
    : baseInputSx;

  const isRequired = textFieldProps.required;

  return (
    <Box sx={labelContainerSx}>
      {label && (
        <Typography
          sx={
            labelSx
              ? ([inputLabelSx, labelSx] as SxProps<Theme>)
              : inputLabelSx
          }
          component="label"
        >
          {label}
          {isRequired && (
            <Box component="span" sx={requiredAsteriskSx}>
              *
            </Box>
          )}
        </Typography>
      )}
      <TextField
        {...(textFieldProps as TextFieldProps)}
        ref={ref}
        sx={mergedSx}
        InputProps={mergedInputProps as MuiInputProps}
        InputLabelProps={mergedLabelProps}
      />
    </Box>
  );
});

Input.displayName = 'Input';

